import React from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import { AgeEnum } from '@rover/rsdk/src/apiClient/latest/model/ageEnum';
import type { LocalizedBrandServicePetType } from '@rover/rsdk/src/apiClient/latest/model/localizedBrandServicePetType';
import type { PetDetails } from '@rover/rsdk/src/apiClient/latest/model/petDetails';
import { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/petTypeEnum';
import getBrandDisplayName from '@rover/shared/js/utils/getBrandDisplayName';
import { PetType } from '@rover/types/src/Pet';
import { ServiceTypeSlug } from '@rover/types/src/ServiceType';

import { PageSectionId, useSectionRef } from '../SeoLocation/hooks/useSectionRef';

import catLookingUpwardImage from './components/images/cat-looking-upward.png';
import dogLookingUpwardImage from './components/images/dog-looking-upward.png';
import RoverProtectCondensed from './components/RoverProtectCondensed';
import ServiceLandingBenefits from './components/ServiceLandingBenefits';
import ServiceLandingFooterLinks from './components/ServiceLandingFooterLinks';
import ServiceLandingHero from './components/ServiceLandingHero';
import ServiceLandingHowBrandWorks from './components/ServiceLandingHowBrandWorks';
import ServiceLandingPageWrapper from './components/ServiceLandingPageWrapper';
import ServiceLandingSearchResultsCarousel from './components/ServiceLandingSearchResultsCarousel';
import ServiceLandingSection, {
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from './components/ServiceLandingSection';
import ServiceLandingServicesWrapper from './components/ServiceLandingServicesWrapper';
import { getServiceLandingSearchFilters } from './utilities/getServiceLandingSearchFilters';
import type { ServiceLandingTemplateProps } from './types';

const getHowBrandWorksPetType = (
  petType: LocalizedBrandServicePetType | undefined,
  age: PetDetails['age']
): PetType | undefined => {
  if (petType === PetTypeEnum.cat) return PetType.CAT;
  if (petType === PetTypeEnum.dog) return age === AgeEnum.adolescent ? PetType.PUPPY : PetType.DOG;
  return undefined;
};

const getOtherServicesImage = (petType: LocalizedBrandServicePetType | undefined): string =>
  petType === PetTypeEnum.cat ? catLookingUpwardImage : dogLookingUpwardImage;

const getOtherServicesHeading = (): MessageDescriptor => {
  const brandDisplayName = getBrandDisplayName();
  return msg`${brandDisplayName} offers many services to meet your pet care needs, including:`;
};

const ServiceLandingPageTemplate: React.FC<ServiceLandingTemplateProps> = ({
  serviceLandingData,
  countryConfig,
  locale,
  serviceTypes,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
  serviceTypeSlug,
  searchResults,
  otherServiceLinks,
}) => {
  const { i18n } = useLingui();
  const otherServicesSectionRef = useSectionRef(PageSectionId.OtherServices);

  const {
    h1,
    h2,
    seoTitle,
    seoMetaDescription,
    internalServiceType,
    petDetails,
    petType,
    serviceBenefitItems,
    serviceBenefitsHeading,
    sentenceCasedServiceTypeName,
    indexable,
  } = serviceLandingData;
  const {
    displayName: serviceTypeName,
    slug: internalServiceTypeSlug,
    providerNounPlural,
  } = internalServiceType;
  const brandName = getBrandDisplayName();
  const isTraining = internalServiceTypeSlug === ServiceTypeSlug.DOG_TRAINING;
  const roverProtectSectionRef = useSectionRef(PageSectionId.RoverGuarantee);

  const searchFilters = getServiceLandingSearchFilters(serviceLandingData);

  return (
    <ServiceLandingPageWrapper
      seoTitle={seoTitle}
      seoMetaDescription={seoMetaDescription}
      serviceTypeName={serviceTypeName}
      brandName={brandName}
      h1={h1}
      h2={h2}
      countryConfig={countryConfig}
      indexable={indexable}
    >
      <ServiceLandingHero
        h1={h1}
        h2={h2}
        locale={locale}
        serviceTypes={serviceTypes}
        internalServiceType={internalServiceType}
        initialSearchFilters={searchFilters}
        petType={petType}
        age={petDetails.age}
        internalServiceTypeSlug={internalServiceTypeSlug}
      />

      <RoverProtectCondensed
        countryConfig={countryConfig}
        isTraining={isTraining}
        htmlRef={roverProtectSectionRef}
        mt="0x"
        mb={SERVICE_LANDING_SECTION_MARGIN_Y}
      />

      <ServiceLandingBenefits
        internalServiceTypeSlug={internalServiceTypeSlug}
        petType={petType}
        age={petDetails.age}
        serviceBenefitItems={serviceBenefitItems}
        serviceBenefitsHeading={serviceBenefitsHeading}
      />
      <ServiceLandingSearchResultsCarousel
        serviceTypeName={sentenceCasedServiceTypeName || serviceTypeName}
        providerNounPlural={providerNounPlural}
        searchResults={searchResults}
        searchFilters={searchFilters}
        locale={locale}
        serviceTypeSlug={serviceTypeSlug}
        countryConfig={countryConfig}
      />

      <ServiceLandingHowBrandWorks
        petType={getHowBrandWorksPetType(petType, petDetails.age)}
        serviceTypeName={sentenceCasedServiceTypeName ?? undefined}
        providerNounPlural={providerNounPlural}
      />

      {otherServiceLinks.length > 0 && (
        <ServiceLandingServicesWrapper
          htmlRef={otherServicesSectionRef}
          serviceLandingType={null}
          serviceLinksWithDisplayName={otherServiceLinks}
          imageSrc={getOtherServicesImage(petType)}
          mt={['12x', '24x', '0x']} // keep the legacy mobile/tablet gap; flush with HowBrandWorks on desktop only
        >
          {i18n._(getOtherServicesHeading())}
        </ServiceLandingServicesWrapper>
      )}

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout />
      </ServiceLandingSection>
      <ServiceLandingFooterLinks
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
      />
    </ServiceLandingPageWrapper>
  );
};

export default ServiceLandingPageTemplate;
